import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { TruncatedText } from "@/components/ui";
import { BookingPartyItemsCell } from "./BookingPartyItemsCell";
import { cn } from "@/lib/utils";
import type { BookingListRow } from "./bookingViewModel";

interface PartnerBookingMobileBodyProps {
  booking: BookingListRow;
  omitVisitDate?: boolean;
  titleMuted?: boolean;
  visitDateLabel: string | null;
  visitTimeLabel: string | null;
  statusBadge: ReactNode;
  paidAmount: ReactNode;
  cancellationSource: ReactNode;
  receiptActions?: ReactNode;
  /** Notes, handshake meta, etc. shown after the services list. */
  children?: ReactNode;
  className?: string;
}

export function PartnerBookingMobileBody({
  booking,
  omitVisitDate = false,
  titleMuted = false,
  visitDateLabel,
  visitTimeLabel,
  statusBadge,
  paidAmount,
  cancellationSource,
  receiptActions,
  children,
  className,
}: PartnerBookingMobileBodyProps) {
  const { t } = useTranslation("bookings");

  const activityName = booking.activityName || t("unknownActivity");

  const timeClassName = cn(
    "shrink-0 text-base font-semibold tabular-nums leading-none tracking-tight",
    titleMuted ? "text-surface-400" : "text-surface-900",
  );

  return (
    <div className={cn("px-4 py-3", className)}>
      {!omitVisitDate ? (
        <p className="mb-1 text-[10px] leading-tight text-surface-500">{visitDateLabel}</p>
      ) : null}

      <div className="space-y-1">
        <div className="flex items-center gap-3">
          <p className={timeClassName}>{visitTimeLabel || "—"}</p>
          <TruncatedText
            as="p"
            className={cn(
              "min-w-0 flex-1 text-base font-semibold leading-snug",
              titleMuted ? "text-surface-500" : "text-surface-900",
            )}
          >
            {booking.customerDisplayName}
          </TruncatedText>
        </div>

        <div className="flex items-start justify-between gap-3">
          <TruncatedText
            as="p"
            className={cn(
              "min-w-0 flex-1 text-xs leading-snug",
              titleMuted ? "text-surface-400" : "text-surface-600",
            )}
          >
            {activityName}
          </TruncatedText>
          {paidAmount ? (
            <div className="shrink-0 text-right">{paidAmount}</div>
          ) : null}
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between gap-3">
        <BookingPartyItemsCell
          booking={booking}
          variant="list"
          className="mt-0 min-w-0 flex-1"
        />
        <div className="shrink-0">{statusBadge}</div>
      </div>

      {cancellationSource ? <div className="mt-2">{cancellationSource}</div> : null}

      {children ? <div className="mt-2 space-y-2">{children}</div> : null}

      {receiptActions ? (
        <div className="mt-3" onClick={(e) => e.stopPropagation()}>
          {receiptActions}
        </div>
      ) : null}
    </div>
  );
}
